import { useState } from 'react'
import { useDispatch } from 'react-redux'
import * as routerActions from 'connected-react-router'

// Actions
import { actions as payActions } from 'modules/pay/actions'
import { actions as subscriptionActions } from 'modules/subscription/actions'

// Components
import { Button, IconPen, Checkbox } from '@tracklib/kit'
import PayButton from 'components/pay/paybutton'
import SubscriptionTerms from 'components/subscribe/subscriptionterms'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useIsDirectPayment, useSubscription } from 'modules/subscription/hooks'
import { useSavedPaymentMethod } from 'modules/pay/hooks'
import { useRequest } from 'modules/requests/hooks'
import { useTracking } from 'modules/tracking/hooks'

// Types
import type { Dispatch, TierOffer, TierPeriod, SubscriptionOrder, Subscription } from 'types'

// Utils
import { priceFormatWithVAT } from 'components/base/price'
import { renderCreditCardIcon, niceName } from 'components/profile/currentplan'
import formatDate, { getDateAfterDaysAndMonths } from 'lib/formatdate'

import styles from './subscribeconfirm.css'

export const PaymentMethod = () => {
  const savedPaymentMethod = useSavedPaymentMethod()
  return (
    <>
      <h4 className={styles.yourOrderTitle}>Payment method</h4>
      <div className={styles.orderSubscription}>
        <div className={styles.orderSubscriptionInfoRow}>
          {savedPaymentMethod ? (
            <>
              <div className={styles.paymentInfo}>
                {renderCreditCardIcon(savedPaymentMethod.variant)}
                <div className={styles.orderSubscriptionInfo}>
                  <h6>
                    {savedPaymentMethod.variant === 'paypal'
                      ? `PayPal (${savedPaymentMethod.customer_name})`
                      : `${niceName(savedPaymentMethod.variant)} ending in ${savedPaymentMethod.card_number}`}
                  </h6>
                  {savedPaymentMethod.exp_month && <p>{`Expires ${savedPaymentMethod.exp_month}/${savedPaymentMethod.exp_year}`}</p>}
                </div>
              </div>
              <Button className={styles.orderEditButton} modal to="paymentmethod">
                <IconPen />
              </Button>
            </>
          ) : (
            <>
              <p>Please add a payment method</p>
              <Button to="paymentmethod" modal datatesting="button_paymentmethodmodal">
                Add payment method
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

interface NextBillInfoProps {
  subscription: Subscription | undefined
  orderTotalPrice: string
  offer: TierOffer
}

export const NextBillInfo = ({ subscription, orderTotalPrice, offer }: NextBillInfoProps) => {
  const trialEndDate = getDateAfterDaysAndMonths(offer.free_days, offer.free_months)
  const hasFreeTier = subscription?.state === 'FREE'
  return (
    <>
      {hasFreeTier && <p>Your card will only be charged after your free trial period is over.</p>}
      {`Your ${hasFreeTier ? 'first' : 'next'} bill is for ${orderTotalPrice} on ${formatDate(
        subscription?.next_renewal_date ? new Date(subscription?.next_renewal_date) : trialEndDate,
      )}`}
    </>
  )
}

interface ImmediatePaymentCheckboxProps {
  period: TierPeriod
  checked: boolean
  onClick: () => void
}

export const ImmediatePaymentCheckbox = ({ checked, onClick, period }: ImmediatePaymentCheckboxProps) => {
  const profile = useProfile()
  return (
    <div className={styles.immediatePayment} datatesting="payment_time">
      <Checkbox name="Direct payment check" value={checked} onChange={onClick} datatesting="direct_payment_checkbox">
        {`I understand that I will be charged ${priceFormatWithVAT(
          profile,
          period.discount_offer_price,
          period.discount_offer_price_inc_vat,
        )} immediately to activate my plan.`}
      </Checkbox>
    </div>
  )
}
interface SubscribeConfirmProps {
  offer: TierOffer
  period: TierPeriod
  subscriptionOrder: SubscriptionOrder
}

const SubscribeConfirm = ({ offer, period, subscriptionOrder }: SubscribeConfirmProps): JSX.Element => {
  const [directPaymentCheck, setDirectPaymentCheck] = useState(false)
  const dispatch = useDispatch() as Dispatch
  const profile = useProfile()
  const subscription = useSubscription()
  const payRequest = useRequest('PAY_SUBSCRIPTION_ORDER')
  const startTrialRequest = useRequest('START_TRIAL')
  const orderTotalPrice = priceFormatWithVAT(profile, period.discount_offer_price, period.discount_offer_price_inc_vat)
  const hasFreeTier = subscription?.state === 'FREE'
  const isDirectPayment = useIsDirectPayment(offer, period.months)
  const savedPaymentMethod = useSavedPaymentMethod()
  const buttonText = hasFreeTier ? 'Start trial' : 'Subscribe'
  const trackEvent = useTracking()
  const trackChangedPlan = () => {
    return trackEvent('changedPlan', {
      eventCategory: 'plans',
      eventAction: 'change plan',
      eventLabel: offer.tier.label || 'unknown',
      eventValue: Math.ceil(period.price_inc_vat || 0),
    })
  }

  return (
    <>
      <h4 className={styles.yourOrderTitle}>Summary</h4>
      <div className={styles.orderSubscription}>
        <div className={styles.orderSubscriptionInfoRow}>
          <div className={styles.orderSubscriptionInfo}>
            <h5>{offer.tier.label}</h5>
            <p>
              <strong>{`${orderTotalPrice}/${period.months === 1 ? 'month' : 'year'}`}</strong>
              {` ${offer.tier.credits_give_per_month || ''} track downloads`}
            </p>
          </div>
          <Button className={styles.orderEditButton} onClick={() => dispatch(routerActions.push('/subscribe'))}>
            <IconPen />
          </Button>
        </div>
        {!isDirectPayment && (
          <div className={styles.orderSubscriptionNextBill} datatesting="payment_time">
            <NextBillInfo subscription={subscription} offer={offer} orderTotalPrice={orderTotalPrice} />
          </div>
        )}
      </div>
      <PaymentMethod />
      {isDirectPayment && (
        <ImmediatePaymentCheckbox period={period} checked={directPaymentCheck} onClick={() => setDirectPaymentCheck(!directPaymentCheck)} />
      )}
      <PayButton
        disabled={(isDirectPayment && (!directPaymentCheck || !subscriptionOrder)) || !savedPaymentMethod}
        isLoading={hasFreeTier ? startTrialRequest.isLoading : payRequest.isLoading}
        buttonText={buttonText}
        onClick={() => {
          if (hasFreeTier) {
            dispatch(subscriptionActions.startTrial(offer.id, period.id)).then(() => {
              trackChangedPlan()
            })
          } else {
            dispatch(payActions.paySubscriptionOrder(subscriptionOrder.id)).then(() => {
              trackChangedPlan()
            })
          }
        }}
        hideSecure={!isDirectPayment}
      />
      <SubscriptionTerms buttonText={buttonText} />
    </>
  )
}

export default SubscribeConfirm
